/*
 * PANEL HUB VERSION F — SHARED BEHAVIOUR
 * Scope: preview/sticky offsets, variant-aware purchase-info refresh, PDP policy anchors,
 * Judge.me initial sort and cart shipping-estimator presentation.
 * Native Prestige product forms, cart and checkout remain authoritative.
 * Maintenance map: assets/phf-developer-handover.txt
 */
(() => {
  'use strict';
  let queued = false;
  let lastPreviewHeight = -1;
  let lastStickyHeight = -1;
  const observed = new WeakSet();
  const selector = '#preview-bar-iframe, #ShopifyPreviewBar, iframe[id*="preview-bar"], iframe[src*="preview-bar"], product-sticky-bar';
  const visibleHeight = element => {
    if (!element || !element.getClientRects().length) return 0;
    const box = element.getBoundingClientRect(), style = getComputedStyle(element);
    return style.visibility !== 'hidden' && style.display !== 'none' && Number(style.opacity) !== 0 && box.bottom > 0 && box.top < innerHeight ? Math.max(0, Math.min(box.bottom, innerHeight) - Math.max(0, box.top)) : 0;
  };
  const observe = element => {
    if (resize && element && !observed.has(element)) {
      observed.add(element);
      resize.observe(element);
    }
  };
  const measure = () => {
    queued = false;
    const preview = document.querySelector('#preview-bar-iframe, #ShopifyPreviewBar, iframe[id*="preview-bar"], iframe[src*="preview-bar"]');
    const sticky = document.querySelector('product-sticky-bar');
    const previewHeight = visibleHeight(preview), stickyHeight = visibleHeight(sticky);
    if (previewHeight !== lastPreviewHeight) {
      lastPreviewHeight = previewHeight;
      document.documentElement.style.setProperty('--phf-preview-offset', previewHeight + 'px');
    }
    if (stickyHeight !== lastStickyHeight) {
      lastStickyHeight = stickyHeight;
      document.documentElement.style.setProperty('--phf-sticky-clearance', stickyHeight + 'px');
    }
    observe(preview);
    observe(sticky);
  };
  const schedule = () => { if (!queued) { queued = true; requestAnimationFrame(measure); } };
  const resize = 'ResizeObserver' in window ? new ResizeObserver(schedule) : null;
  addEventListener('resize', schedule, { passive: true });
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('cart:change', schedule);
  const mutation = new MutationObserver(records => {
    for (const record of records) {
      if (record.type === 'attributes') {
        if (record.target.matches && record.target.matches(selector)) { schedule(); return; }
        continue;
      }
      for (const node of [...record.addedNodes, ...record.removedNodes]) {
        if (node.nodeType !== 1) continue;
        if ((node.matches && node.matches(selector)) || (node.querySelector && node.querySelector(selector))) { schedule(); return; }
      }
    }
  });
  mutation.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'open', 'style', 'class'] });
  schedule();
  setTimeout(schedule, 250);
  setTimeout(schedule, 1000);
})();

(() => {
  'use strict';
  if (customElements.get('phf-purchase-info')) return;
  class PurchaseInfo extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      document.addEventListener('variant:change', e => {
        if (!e.target || e.target.getAttribute('id') !== this.dataset.form) return;
        const variant = e.detail && e.detail.variant;
        if (!variant || String(variant.id) === this.dataset.variant) return;
        this.refresh(variant.id);
      }, { signal: this.events.signal, capture: true });
    }
    disconnectedCallback() { this.events.abort(); if (this.request) this.request.abort(); }
    async refresh(variantId) {
      if (this.request) this.request.abort();
      const request = this.request = new AbortController();
      this.setAttribute('aria-busy', 'true');
      const status = this.querySelector('[data-phf-refresh-status]');
      status.hidden = false; status.textContent = 'Updating dimensions and coverage...';
      this.querySelectorAll('phf-calculator details,.phf-specs,.phf-unit-line,.phf-key-details').forEach(el => { el.hidden = true; });
      try {
        const url = new URL(location.href);
        url.searchParams.set('variant', variantId); url.searchParams.set('section_id', this.dataset.section);
        const response = await fetch(url, { signal: request.signal, credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error('Unable to load this selection.');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = page.getElementById(this.id);
        if (!fresh || fresh.dataset.variant !== String(variantId)) throw new Error('Coverage could not be verified.');
        if (!this.isConnected || request !== this.request) return;
        this.dataset.variant = fresh.dataset.variant;
        this.innerHTML = fresh.innerHTML;
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) status.textContent = 'Please reload this product to verify dimensions and coverage for your selection, or contact our team.';
      } finally { if (this.isConnected && request === this.request) this.removeAttribute('aria-busy'); }
    }
  }
  customElements.define('phf-purchase-info', PurchaseInfo);
})();

(() => {
  'use strict';
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const showSection = id => {
    const target = document.getElementById(id);
    if (!target) return false;
    target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    if (history.replaceState) history.replaceState(null, '', '#' + id);
    return true;
  };
  document.addEventListener('click', event => {
    const link = event.target.closest && event.target.closest('a[href]');
    if (!link) return;
    let url;
    try { url = new URL(link.href, location.href); } catch (_) { return; }
    if (url.origin !== location.origin) return;
    const path = url.pathname.replace(/\/$/, '');
    if (document.body.classList.contains('phf-page-product')) {
      if (path === '/policies/shipping-policy' && showSection('phf-delivery')) { event.preventDefault(); return; }
      if (path === '/policies/refund-policy' && showSection('phf-returns')) { event.preventDefault(); return; }
    }
  });
})();

/* Version F only: make Judge.me's initial product-review ordering conversion-friendly without hiding any review. */
(() => {
  'use strict';
  if (!document.body.classList.contains('phf-page-product')) return;
  let attempts = 0;
  const timer = setInterval(() => {
    attempts += 1;
    const select = Array.from(document.querySelectorAll('select')).find(candidate =>
      Array.from(candidate.options || []).some(option => {
        const text = String(option.textContent || '').trim().toLowerCase();
        const value = String(option.value || '').trim().toLowerCase();
        return text === 'highest rating' || (value.includes('highest') && value.includes('rating'));
      })
    );
    if (!select) {
      if (attempts >= 60) clearInterval(timer);
      return;
    }
    const options = Array.from(select.options || []);
    const highest = options.find(option => {
      const text = String(option.textContent || '').trim().toLowerCase();
      const value = String(option.value || '').trim().toLowerCase();
      return text === 'highest rating' || (value.includes('highest') && value.includes('rating'));
    });
    const selected = select.selectedOptions && select.selectedOptions[0];
    const currentText = String(selected && selected.textContent || '').trim().toLowerCase();
    const currentValue = String(select.value || '').trim().toLowerCase();
    if (highest && (currentText === 'most recent' || (currentValue.includes('recent') && !currentValue.includes('highest')))) {
      select.value = highest.value;
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    clearInterval(timer);
  }, 250);
})();

/* Version F cart QA: tidy native shipping-estimator output without changing the actual Shopify rates. */
(() => {
  'use strict';
  if (!document.body.classList.contains('phf-page-cart')) return;

  const cleanShippingResults = root => {
    if (!root) return;
    const region = root.querySelector('[role="region"]');
    if (!region || region.hidden) return;

    region.querySelectorAll('li').forEach(item => {
      const nodes = Array.from(item.childNodes);
      nodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          node.textContent = node.textContent.replace(/^\s*,\s*|\s*,\s*$/g, '');
        }
      });
      item.innerHTML = item.innerHTML.replace(/(^|>)\s*,\s*(?=<|$)/g, '$1').replace(/\s*,\s*$/g, '');
    });
  };

  const attach = estimator => {
    if (!estimator || estimator.dataset.phfCartQc === 'true') return;
    estimator.dataset.phfCartQc = 'true';
    cleanShippingResults(estimator);
    const observer = new MutationObserver(() => cleanShippingResults(estimator));
    observer.observe(estimator, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['hidden'] });
  };

  document.querySelectorAll('shipping-estimator').forEach(attach);
  document.addEventListener('shopify:section:load', () => document.querySelectorAll('shipping-estimator').forEach(attach));
})();
